import { useMemo } from 'react';
import PlanningRdvCard from './PlanningRdvCard';
import {
  intervenantDisplayName,
  parisMinutesFromMidnight,
  rdvDurationMinutes,
} from '../../utils/planningDayParis';
import { avatarClass, memberInitials, memberRole } from './planningPeople';

const PX_PER_MIN = 1.45;
const UNASSIGNED_KEY = '__unassigned__';

function computeWindow(rdvs) {
  if (!rdvs.length) return { start: 9 * 60, end: 19 * 60 };
  let start = 24 * 60;
  let end = 0;
  for (const rdv of rdvs) {
    const s = parisMinutesFromMidnight(rdv.dateDebut);
    const e = s + rdvDurationMinutes(rdv);
    start = Math.min(start, s);
    end = Math.max(end, e);
  }
  start = Math.floor(start / 60) * 60;
  end = Math.ceil(end / 60) * 60;
  start = Math.max(7 * 60, start);
  end = Math.min(22 * 60, Math.max(end, 19 * 60, start + 8 * 60));
  return { start, end };
}

function hourLabels(startMin, endMin) {
  const labels = [];
  for (let m = startMin; m <= endMin; m += 60) labels.push(m);
  return labels;
}

function formatHour(minutes) {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:00`;
}

/**
 * Timeline jour : colonnes des professionnels cochés.
 * Les rendez-vous non assignés ne forment pas une colonne.
 */
export default function PlanningDayTimeline({
  columns,
  rdvsByColumn,
  onRdvClick,
  loading = false,
}) {
  const allRdvs = useMemo(
    () => Object.values(rdvsByColumn || {}).flat(),
    [rdvsByColumn],
  );

  const { start, end } = useMemo(() => computeWindow(allRdvs), [allRdvs]);
  const height = Math.max((end - start) * PX_PER_MIN, 420);
  const hours = hourLabels(start, end);

  if (loading) {
    return (
      <div className="flex h-80 items-center justify-center rounded-2xl border border-gray-100 bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-violet-600" />
      </div>
    );
  }

  if (!columns?.length) {
    return (
      <div className="flex h-72 items-center justify-center rounded-2xl border border-gray-100 bg-white px-6 text-center">
        <div>
          <p className="text-sm font-semibold text-gray-800">Aucun professionnel affiché</p>
          <p className="mt-1 text-xs text-gray-500">
            Cochez un professionnel pour afficher sa colonne.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="overflow-x-auto">
        <div
          className="grid min-w-[520px]"
          style={{
            gridTemplateColumns: `52px repeat(${columns.length}, minmax(188px, 1fr))`,
          }}
        >
          <div className="border-b border-gray-100 bg-white" />
          {columns.map((col) => {
            const member = col.member;
            const initials = memberInitials(member || { prenom: col.label });
            return (
              <div
                key={col.key}
                className="flex items-center gap-2.5 border-b border-l border-gray-100 px-3 py-3"
              >
                <span
                  className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${avatarClass(col.key)}`}
                >
                  {initials}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">{col.label}</p>
                  <p className="truncate text-[11px] text-gray-500">{memberRole(member)}</p>
                </div>
              </div>
            );
          })}

          <div className="relative bg-white" style={{ height }}>
            {hours.map((m) => (
              <div
                key={m}
                className="absolute right-2 -translate-y-1/2 text-[11px] font-medium tabular-nums text-gray-400"
                style={{ top: (m - start) * PX_PER_MIN }}
              >
                {formatHour(m)}
              </div>
            ))}
          </div>

          {columns.map((col) => {
            const items = rdvsByColumn[col.key] || [];
            return (
              <div
                key={col.key}
                className="relative border-l border-gray-50 bg-white"
                style={{ height }}
              >
                {hours.map((m) => (
                  <div
                    key={`${col.key}-${m}`}
                    className="pointer-events-none absolute inset-x-3 border-t border-slate-100/70"
                    style={{ top: (m - start) * PX_PER_MIN }}
                  />
                ))}
                {items.map((rdv) => {
                  const s = parisMinutesFromMidnight(rdv.dateDebut);
                  const dur = rdvDurationMinutes(rdv);
                  const top = (s - start) * PX_PER_MIN + 3;
                  const h = Math.max(dur * PX_PER_MIN - 8, 108);
                  return (
                    <div
                      key={rdv.id}
                      className="absolute inset-x-2 z-[1]"
                      style={{ top, height: h }}
                    >
                      <PlanningRdvCard rdv={rdv} onClick={onRdvClick} compact={h < 128} />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { UNASSIGNED_KEY };

export function buildPlanningColumns(members, { selectedIds }) {
  const intervenants = (members || []).filter((m) => m.isIntervenant && m.instructeurId);
  return intervenants
    .filter((m) => !selectedIds || selectedIds.has(m.instructeurId))
    .map((m) => ({
      key: m.instructeurId,
      label: intervenantDisplayName(m),
      member: m,
    }));
}
